import { BookmarkBorderOutlined, Check, Close, FeedOutlined, SavingsOutlined, SentimentSatisfiedAltOutlined, TaskAltOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Box, Grid, List, ListItem, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAWSWebsocket } from "../../../Hooks/useAWSWebsocket";
import { useUser } from "../../../Hooks/useUser";
import { useGetClientDashboardQuery } from "../../../Redux/api/clientApi";
import { setJobPostingModalOpen } from "../../../Redux/slices/JobPostingCreationSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { KaziCard } from "../../../Shared Components/Kazi/Cards/KaziCard";
import { VerificationStatus } from "../../../Types/General";
import { getPartOfDay } from "../../../Utils/Time";
import { ClientCreationModal } from "./ProfileCreation/Client/ClientCreationModal";
import { toPascalCase } from '../../../Utils/String';
import DashboardLayout from '../../../Shared Components/Kazi/Layouts/DashboardLayout';
import { useVeriff } from '../../../Hooks/useVeriff';
import { setShowUnverifiedModal } from '../../../Redux/slices/GlobalModalSlice';
  
export const ClientDashboard = () => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const { mountVeriff, getVerificationDetails } = useVeriff();
    const match = useMediaQuery(theme.breakpoints.down('lg'));
    const twoColumnMatch = useMediaQuery(theme.breakpoints.down('md'));
    const oneColumnMatch = useMediaQuery(theme.breakpoints.down('sm'));
    const [clientCreationOpen, setClientCreationOpen] = useState(false);
    const { 
        data: clientDashboardStats, 
        isLoading: clientDashboardStatsLoading, 
        isFetching: clientDashboardStatsFetching 
    } = useGetClientDashboardQuery();
    const { user, isVerified } = useUser();  
    /* ******************** Variables ******************** */
    const partOfDay = getPartOfDay()
    const verification_status = useAppSelector( state => state.Websocket.verification_status);
    const statsLoading = useMemo(() =>{
        return clientDashboardStatsLoading || clientDashboardStatsFetching;
    }, [clientDashboardStatsLoading, clientDashboardStatsFetching])
    const gridLayout = useMemo(() => {
        if (oneColumnMatch) return `1fr`
        if (twoColumnMatch) return `repeat(2, 1fr)`
        if (match) return `minmax(290px, 368px)`
        return `repeat(auto-fit, minmax(290px, 368px))`
    }, [match, twoColumnMatch, oneColumnMatch])
    /* ******************** Functions ******************** */
    
    const formatLastJobPosting = (title: string) => {
        const regTest = /^[aeiou]$/i
        const startsWithVowel = (regTest).test(title.charAt(0));
        return `Last posted for ${startsWithVowel ? 'an':'a'} ${title}`
    }
    const handlePostJobClicked = () => {
        if (isVerified) {
            navigate('/post-creation');
            // dispatch(setJobPostingModalOpen(true));
            return
        }
        dispatch(setShowUnverifiedModal(true));
    }
    /* ******************** Effects ******************** */
    useEffect(() =>{
        const veriffElement = document.querySelector('#veriff-root');

        if (!statsLoading 
            && verification_status !== VerificationStatus.verified 
            && verification_status !== VerificationStatus.pending
            && veriffElement  ) mountVeriff();
    }, [statsLoading, verification_status])
    /* ******************** JSX ******************** */
    const column1 = [
        {
            title: clientDashboardStats?.profileComplete ? "Profile Complete" : "Complete your profile",
            progress: clientDashboardStats?.profileCompleteness,
            subtitle: "A complete profile allows Talent to learn about the job you're hiring for and easily apply.",
            list: (clientDashboardStats?.profileCompleteness ?? 0) < 100 && (
            <List dense>
                {/**@ts-ignore*/}
                <ListItem >{clientDashboardStats?.completedSections?.description ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}About Me</ListItem>
                {/**@ts-ignore*/}
                <ListItem >{clientDashboardStats?.completedSections?.languages ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Languages</ListItem>
                <ListItem >{clientDashboardStats?.completedSections?.company ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Company Name</ListItem>
                <ListItem>{clientDashboardStats?.completedSections?.profilePicture ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Profile Picture</ListItem>
            </List>
            ),
            icon: <SentimentSatisfiedAltOutlined />,
            button: clientDashboardStats?.profileComplete ? {label: "View Profile", action: () => navigate('/profile/client'), disabled: false}: {label: "Continue", action: () => setClientCreationOpen(true), disabled: false} ,
            gridRow: "1 / span 2",
            gridColumn: "1",
            marginTop: 0,
        },
    ]
    const column2 = [
        {
            title: `${clientDashboardStats?.numberOfJobsPosted} posted ${clientDashboardStats?.numberOfJobsPosted !== 1 ? "jobs": "job"}`,
            icon: <FeedOutlined />,
            button: {label: "Post a job", action: handlePostJobClicked, disabled: false},
            subtitle: formatLastJobPosting(clientDashboardStats?.lastJobPosting ?? ''),
            secondaryButton: (clientDashboardStats?.numberOfJobsPosted ?? 0) > 0 ? {label: "Review", action: () => navigate('/job-postings'), disabled: !isVerified} : undefined,
            gridRow: "1",
            gridColumn: "2",
            marginTop: twoColumnMatch ? 3 : 0
        }, 
        {
            title: `${clientDashboardStats?.numberOfBookmarkedTalents} bookmarked ${clientDashboardStats?.numberOfBookmarkedTalents !== 1 ? "Talents" : "Talent"}`,
            icon: <BookmarkBorderOutlined />,
            subtitle: clientDashboardStats?.lastBookmarkedTalent ? `Last talent was ${clientDashboardStats.lastBookmarkedTalent}`: "",
            gridRow: twoColumnMatch ? "3" : "2",
            gridColumn: "2",
            marginTop: 3,
            button: (clientDashboardStats?.numberOfBookmarkedTalents ?? 0) > 0 ? {label: "View", action: () => navigate('/bookmarks'), disabled: false} : undefined,
        }, 
        {
            title: `${clientDashboardStats?.numberOfActiveJobs} active ${clientDashboardStats?.numberOfActiveJobs !== 1 ? "jobs": "job"}`,
            icon: <TaskAltOutlined />,
            // subtitle: clientDashboardStats.activeJobs.length > 0 ? `With ${clientDashboardStats.activeJobs.join(', ')}`: "",
            gridRow: "3",
            gridColumn: twoColumnMatch ? "1" : "2",
            button: (clientDashboardStats?.numberOfActiveJobs ?? 0) > 0  ? {label: "View", action: () => navigate('/active-jobs'), disabled: false} : undefined,
            marginTop: 3,
        },
    ]
    const column3 = [ 
        {
            title: "0 upcoming payments",
            icon: <SavingsOutlined />,
            gridRow: twoColumnMatch ? "2" : "1",
            gridColumn: twoColumnMatch ? "2" : "3",
            marginTop: twoColumnMatch ? 3 : 0,
            disabled: true
        },  
        {
            icon: <VerifiedOutlined />,
            gridRow: twoColumnMatch ? "4" : "1 / span 2",
            gridColumn: twoColumnMatch ? "1" : "3",
            marginTop: twoColumnMatch ? 2 : 15,
            ...getVerificationDetails()
        }
    ]
    return (
        <>
            <Grid>
                {
                    statsLoading ? 
                    <Skeleton variant='rectangular'sx={{marginBottom: 2, width: "248px"}}/> :
                    <Typography variant="lora-headline-small"> 
                        Good {`${partOfDay?.label}, ${toPascalCase(user?.first_name ?? '') ?? user?.company}`} <img src={partOfDay?.icon} height={'20px'}/>
                    </Typography>
                }
                <DashboardLayout>
                    <Box 
                        className='DashboardColumn'
                    >
                    {
                        column1.map((card, idx) =>(
                                <KaziCard 
                                    key={idx}
                                    title={card.title}
                                    progress={card?.progress}
                                    list={card?.list}
                                    subtitle={card.subtitle}
                                    icon={card?.icon}
                                    button={card?.button}
                                    //@ts-ignore, TODO: comeback and fix the type error when you have time
                                    secondaryButton={card?.secondaryButton}
                                    isLoading={statsLoading}
                                />
                        ))
                    }
                    </Box>
                    <Box 
                        className='DashboardColumn'
                    >
                    {
                        column2.map((card, idx) =>(
                                <KaziCard 
                                    key={idx}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    icon={card?.icon}
                                    button={card?.button}
                                    //@ts-ignore, TODO: comeback and fix the type error when you have time
                                    secondaryButton={card?.secondaryButton}
                                    isLoading={statsLoading}
                                />
                        ))
                    }
                    </Box>
                    <Box 
                        className='DashboardColumn'
                    >
                    {
                        column3.map((card, idx) =>(
                                <KaziCard 
                                    key={idx}
                                    title={card.title}
                                    //@ts-ignore
                                    subtitle={card?.subtitle}
                                    icon={card?.icon}
                                    //@ts-ignore
                                    button={card?.button}
                                    //@ts-ignore
                                    veriff={card?.veriff}
                                    //@ts-ignore, TODO: comeback and fix the type error when you have time
                                    secondaryButton={card?.secondaryButton}
                                    isLoading={statsLoading}
                                />
                        ))
                    }
                    </Box>
                </DashboardLayout>
            </Grid>
            <ClientCreationModal open={clientCreationOpen} setOpen={setClientCreationOpen} />
        </>
    )
}